import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  CalendarPlus,
  CheckCircle2,
  Clock,
  Eye,
  MapPin,
  Search,
  Users,
} from 'lucide-react';
import { getApiBaseUrl } from '../../config/api';
import { formatEventPlace, formatEventWhen, isEventClosed } from './calendarLinks';
import './IncomingEventsPanel.css';

function isUpcomingEvent(event) {
  return !isEventClosed(event);
}

/**
 * Attend Events board shown from the header "Incoming events" control.
 * Guests can browse details; RSVP asks them to log in first.
 */
export default function IncomingEventsPanel({ user = null, onNavigate }) {
  const API_URL = getApiBaseUrl();
  const navigate = useNavigate();
  const isLoggedIn = Boolean(user?.id);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [range, setRange] = useState('upcoming');
  const [loginPrompt, setLoginPrompt] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const q = new URLSearchParams({ limit: '100' });
      if (user?.id) q.set('user_id', String(user.id));
      const res = await fetch(`${API_URL}/get_attendance_events.php?${q}`);
      const data = await res.json();
      const list = data.success && Array.isArray(data.events) ? data.events : [];
      list.sort((a, b) => {
        const upcomingA = isUpcomingEvent(a) ? 0 : 1;
        const upcomingB = isUpcomingEvent(b) ? 0 : 1;
        if (upcomingA !== upcomingB) return upcomingA - upcomingB;
        const ta = Date.parse(`${a.event_date || ''}T00:00:00`) || 0;
        const tb = Date.parse(`${b.event_date || ''}T00:00:00`) || 0;
        if (ta !== tb) return upcomingA === 0 ? ta - tb : tb - ta;
        return Number(b.id || 0) - Number(a.id || 0);
      });
      setEvents(list);
      if (!data.success) setError('Unable to load events right now.');
    } catch (err) {
      console.error('Incoming events panel load error:', err);
      setError('Unable to load events right now.');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [API_URL, user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const upcomingEvents = useMemo(() => events.filter(isUpcomingEvent), [events]);
  const pastEvents = useMemo(() => events.filter((event) => !isUpcomingEvent(event)), [events]);

  const scoped = useMemo(() => {
    if (range === 'upcoming') return upcomingEvents;
    if (range === 'past') return pastEvents;
    return events;
  }, [range, upcomingEvents, pastEvents, events]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return scoped;
    return scoped.filter((event) =>
      [event.title, event.description, event.location, event.country, event.organizer]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [scoped, query]);

  const coverUrl = (path) => {
    if (!path) return '';
    if (/^https?:\/\//i.test(path)) return path;
    return `${API_URL}/${String(path).replace(/^\/+/, '')}`;
  };

  const goToEvent = (eventId) => {
    onNavigate?.();
    navigate(`/event-attendance/${eventId}`);
  };

  const handleView = (eventId) => {
    goToEvent(eventId);
  };

  const handleAttend = (eventId) => {
    if (!isLoggedIn) {
      setLoginPrompt(true);
      return;
    }
    goToEvent(eventId);
  };

  return (
    <div className="iep-panel">
      <div className="iep-toolbar">
        <div className="iep-search-wrap">
          <Search size={18} strokeWidth={2.2} />
          <input
            type="search"
            className="iep-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search events by title, place, or organizer"
            aria-label="Search incoming events"
          />
        </div>

        <div className="iep-filters" role="tablist" aria-label="Event time range">
          <FilterChip
            active={range === 'upcoming'}
            onClick={() => setRange('upcoming')}
            label="Upcoming"
            count={upcomingEvents.length}
          />
          <FilterChip
            active={range === 'past'}
            onClick={() => setRange('past')}
            label="Past"
            count={pastEvents.length}
          />
          <FilterChip
            active={range === 'all'}
            onClick={() => setRange('all')}
            label="All"
            count={events.length}
          />
        </div>
      </div>

      {!loading && !error && (
        <p className="iep-meta">
          <Calendar size={14} strokeWidth={2.2} />
          <span>
            {filtered.length} {filtered.length === 1 ? 'event' : 'events'}
            {query.trim() ? ` matching “${query.trim()}”` : ''}
          </span>
        </p>
      )}

      <div className="iep-list-wrap">
        {loading ? (
          <div className="iep-empty">
            <strong>Loading events</strong>
            Finding gatherings you can attend…
          </div>
        ) : error ? (
          <div className="iep-empty iep-empty--error">
            <strong>Could not load events</strong>
            {error}
          </div>
        ) : events.length === 0 ? (
          <div className="iep-empty">
            <strong>No events yet</strong>
            Check back soon for community gatherings you can attend.
          </div>
        ) : filtered.length === 0 && range === 'upcoming' && !query.trim() ? (
          <div className="iep-empty">
            <strong>No upcoming events</strong>
            There are no incoming events right now.
            {pastEvents.length > 0 ? (
              <button type="button" className="iep-empty-link" onClick={() => setRange('past')}>
                View {pastEvents.length} past {pastEvents.length === 1 ? 'event' : 'events'}
              </button>
            ) : null}
          </div>
        ) : filtered.length === 0 ? (
          <div className="iep-empty">
            <strong>No matches</strong>
            No events match that search.
          </div>
        ) : (
          <ul className="iep-list">
            {filtered.map((event) => {
              const going = Boolean(event.is_attending);
              const upcoming = isUpcomingEvent(event);
              const place = formatEventPlace(event);
              const excerpt = String(event.description || '').replace(/\s+/g, ' ').trim();
              return (
                <li key={event.id} className="iep-card">
                  <div className="iep-cover">
                    {event.cover_image ? (
                      <img src={coverUrl(event.cover_image)} alt="" />
                    ) : null}
                    {!upcoming ? (
                      <span className="iep-cover-badge is-past">
                        <Clock size={12} strokeWidth={2.4} />
                        Closed
                      </span>
                    ) : going ? (
                      <span className="iep-cover-badge is-going">
                        <CheckCircle2 size={12} strokeWidth={2.4} />
                        You’re going
                      </span>
                    ) : (
                      <span className="iep-cover-badge">
                        <Calendar size={12} strokeWidth={2.4} />
                        Upcoming
                      </span>
                    )}
                  </div>
                  <div className="iep-body">
                    <h3 className="iep-title">{event.title || 'Untitled event'}</h3>
                    <div className="iep-details">
                      <p className="iep-meta-row">
                        <span className="iep-meta-icon">
                          <Calendar size={14} strokeWidth={2.2} />
                        </span>
                        <span>{formatEventWhen(event)}</span>
                      </p>
                      {place ? (
                        <p className="iep-meta-row">
                          <span className="iep-meta-icon">
                            <MapPin size={14} strokeWidth={2.2} />
                          </span>
                          <span>{place}</span>
                        </p>
                      ) : null}
                      <p className="iep-meta-row">
                        <span className="iep-meta-icon">
                          <Users size={14} strokeWidth={2.2} />
                        </span>
                        <span>{Number(event.attendee_count || 0)} attending</span>
                      </p>
                    </div>
                    {excerpt ? <p className="iep-excerpt">{excerpt}</p> : null}
                    <div className="iep-actions">
                      <button
                        type="button"
                        className="iep-action iep-action--ghost"
                        onClick={() => handleView(event.id)}
                      >
                        <Eye size={15} strokeWidth={2.2} />
                        View details
                      </button>
                      {upcoming || going ? (
                        <button
                          type="button"
                          className={`iep-action iep-action--primary${going ? ' is-going' : ''}`}
                          onClick={() => handleAttend(event.id)}
                        >
                          {going ? (
                            <>
                              <CheckCircle2 size={15} strokeWidth={2.2} />
                              View RSVP
                            </>
                          ) : (
                            <>
                              <CalendarPlus size={15} strokeWidth={2.2} />
                              Attend Event
                            </>
                          )}
                        </button>
                      ) : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {loginPrompt && !isLoggedIn && (
        <div
          className="iep-login-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Login required"
          onClick={() => setLoginPrompt(false)}
        >
          <div className="iep-login-card" onClick={(e) => e.stopPropagation()}>
            <h4>Log in to continue</h4>
            <p>Please log in first to attend this event and save it to your calendar.</p>
            <div className="iep-login-actions">
              <button
                type="button"
                className="iep-action iep-action--primary"
                onClick={() => {
                  window.location.href = '/login';
                }}
              >
                Log in
              </button>
              <button
                type="button"
                className="iep-action iep-action--ghost"
                onClick={() => setLoginPrompt(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterChip({ active, onClick, label, count }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      className={`iep-chip${active ? ' is-active' : ''}`}
      onClick={onClick}
    >
      {label}
      <span className="iep-chip-count">{count}</span>
    </button>
  );
}
